import { useEffect, useState } from "react";
import { api, fmtEur, fmtDate, formatErr, canWrite, COMPANY_COLORS } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useCompany } from "@/contexts/CompanyContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { MonthPicker } from "@/components/MonthPicker";
import { Allegati } from "@/components/Allegati";
import { PagamentoDialog, PAG_CAT } from "@/components/PagamentoDialog";
import { PagaDialog } from "@/components/PagaDialog";
import { padrLabel } from "@/components/PadroncinoSelect";
import { stato } from "@/pages/Scadenzario";
import { Plus, Pencil, Trash2, Search, CircleCheck, Undo2, Globe } from "lucide-react";
import { toast } from "sonner";

const STATI = [{ v: "da_pagare", l: "Da pagare" }, { v: "pagato", l: "Pagati" }, { v: "tutti", l: "Tutti" }];

export default function Pagamenti() {
  const { company } = useCompany();
  const { user } = useAuth();
  const writable = canWrite(user, "movimenti");
  const [items, setItems] = useState([]);
  const [padroncini, setPadroncini] = useState([]);
  const [banche, setBanche] = useState([]);
  const [cat, setCat] = useState("tutti");
  const [st, setSt] = useState("da_pagare");
  const [mese, setMese] = useState("");
  const [q, setQ] = useState("");
  const [dlg, setDlg] = useState(null);
  const [paga, setPaga] = useState(null);
  const [delP, setDelP] = useState(null);

  const load = () => api.get("/pagamenti", { params: { company } }).then((r) => setItems(r.data));
  useEffect(() => {
    load();
    api.get("/padroncini", { params: { company } }).then((r) => setPadroncini(r.data));
    api.get("/banche", { params: { company } }).then((r) => setBanche([...new Set(r.data.map((b) => b.abbreviazione))]));
  }, [company]); // eslint-disable-line

  const chi = (p) => p.categoria === "padroncini" ? padrLabel(padroncini.find((x) => x.id === p.padroncino_id)) : p.beneficiario;
  const t = q.trim().toLowerCase();
  const base = items.filter((p) => (st === "tutti" || p.stato === st) && (!mese || p.data_scadenza.startsWith(mese)) &&
    (!t || `${chi(p) || ""} ${p.descrizione} ${p.numero_fattura || ""} ${p.codice_avviso || ""}`.toLowerCase().includes(t)));
  const shown = base.filter((p) => cat === "tutti" || p.categoria === cat);
  const totale = shown.reduce((a, p) => a + p.importo, 0);

  const del = async (p, serie) => {
    setDelP(null);
    try { await api.delete(`/pagamenti/${p.id}`, { params: { serie } }); toast.success("Eliminato"); load(); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };
  const annulla = async (p) => {
    if (!window.confirm("Annullare il pagamento? Se registrato, il movimento in Prima Nota verrà eliminato.")) return;
    try { await api.post(`/pagamenti/${p.id}/annulla`); toast.success("Pagamento annullato"); load(); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };
  const webdesk = async (p) => {
    try { await api.patch(`/pagamenti/${p.id}`, { webdesk_segnato: !p.webdesk_segnato }); load(); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-heading font-black">Pagamenti da fare</h1>
          <div className="text-sm text-muted-foreground">Fatture, F24 e rate da pagare, con PDF allegati. Al pagamento puoi registrarli in Prima Nota.</div>
        </div>
        <div className="flex gap-2">
          <MonthPicker value={mese} onChange={setMese} allowClear testId="pag-mese" />
          {writable && <Button size="sm" className="h-8 gap-1" onClick={() => setDlg({})} data-testid="pag-new-button"><Plus className="w-4 h-4" />Nuovo pagamento</Button>}
        </div>
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <Tabs value={cat} onValueChange={setCat}>
          <TabsList className="flex-wrap h-auto">
            {[{ value: "tutti", label: "Tutti" }, ...PAG_CAT].map((c) => (
              <TabsTrigger key={c.value} value={c.value} data-testid={`pag-tab-${c.value}`}>
                {c.label} ({base.filter((p) => c.value === "tutti" || p.categoria === c.value).length})
              </TabsTrigger>))}
          </TabsList>
        </Tabs>
        <div className="flex gap-1">{STATI.map((s) => (
          <Button key={s.v} size="sm" variant={st === s.v ? "default" : "outline"} className="h-8" onClick={() => setSt(s.v)} data-testid={`pag-stato-${s.v}`}>{s.l}</Button>))}
        </div>
        <div className="relative w-64">
          <Search className="w-3.5 h-3.5 absolute left-2.5 top-2.5 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca beneficiario, fattura..." className="h-8 pl-7" data-testid="pag-search" />
        </div>
        <div className="ml-auto text-sm">Totale: <span className="font-mono font-bold" data-testid="pag-totale">{fmtEur(totale)}</span></div>
      </div>

      <Card className="rounded-md overflow-x-auto">
        <table className="autotrans-table w-full" data-testid="pag-table">
          <thead><tr><th>Scadenza</th><th>Mancano</th><th>Categoria</th><th>Beneficiario</th><th>Descrizione</th><th>Fattura / codice</th>
            <th className="text-right">Importo</th><th>Banca</th><th>Stato</th><th>PDF</th><th className="w-40">Azioni</th></tr></thead>
          <tbody>
            {shown.length === 0 && <tr><td colSpan={11} className="text-center py-6 text-muted-foreground">Nessun pagamento</td></tr>}
            {shown.map((p) => <Riga key={p.id} p={p} chi={chi(p)} writable={writable} onEdit={() => setDlg({ edit: p })}
                                    onDel={() => setDelP(p)} onPaga={() => setPaga(p)} onAnnulla={() => annulla(p)} onWebdesk={() => webdesk(p)} onReload={load} />)}
          </tbody>
        </table>
      </Card>

      <PagamentoDialog open={!!dlg} edit={dlg?.edit} onClose={() => setDlg(null)} onSaved={() => { setDlg(null); load(); }}
                       company={company} categoria={cat} padroncini={padroncini} banche={banche} />
      <PagaDialog p={paga} banche={banche} onClose={() => setPaga(null)} onDone={() => { setPaga(null); load(); }} />
      <Dialog open={!!delP} onOpenChange={(o) => !o && setDelP(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Eliminare il pagamento?</DialogTitle></DialogHeader>
          {delP && <div className="text-sm">{delP.beneficiario || delP.descrizione} · {fmtEur(delP.importo)}{delP.rate_tot > 1 && ` (${delP.rata_n}/${delP.rate_tot})`}</div>}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDelP(null)}>Annulla</Button>
            {delP?.rate_tot > 1 && <Button variant="destructive" onClick={() => del(delP, true)} data-testid="pag-del-serie">Tutte le rate non pagate</Button>}
            <Button variant="destructive" onClick={() => del(delP, false)} data-testid="pag-del-uno">{delP?.rate_tot > 1 ? "Solo questa rata" : "Elimina"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Riga = ({ p, chi, writable, onEdit, onDel, onPaga, onAnnulla, onWebdesk, onReload }) => {
  const s = stato(p.data_scadenza);
  const pagato = p.stato === "pagato";
  return (
    <tr data-testid={`pag-row-${p.id}`} className={pagato ? "opacity-70" : ""}>
      <td className="font-mono">{fmtDate(p.data_scadenza)}</td>
      <td>{pagato ? "" : <Badge className={`${s.cls} text-white h-5 whitespace-nowrap`}>{s.txt}</Badge>}</td>
      <td className="whitespace-nowrap"><Badge variant="outline" className="h-5">{PAG_CAT.find((c) => c.value === p.categoria)?.label}</Badge>
        <Badge style={{ backgroundColor: COMPANY_COLORS[p.company], color: "white" }} className="h-5 ml-1">{p.company}</Badge></td>
      <td>{chi || "—"}</td>
      <td>{p.descrizione}{p.rate_tot > 1 && <span className="ml-1 text-xs text-muted-foreground" data-testid={`pag-rata-${p.id}`}>
        ({p.modalita === "mensile" ? "mese" : "rata"} {p.rata_n}/{p.rate_tot})</span>}</td>
      <td className="font-mono text-xs">{p.numero_fattura}{p.codice_avviso && <div className="text-muted-foreground">{p.codice_avviso}</div>}</td>
      <td className="text-right font-mono">{fmtEur(p.importo)}</td>
      <td className="text-xs">{p.banca || "—"}</td>
      <td className="whitespace-nowrap">{pagato
        ? <div><Badge className="bg-green-600 h-5">Pagato {fmtDate(p.data_pagamento)}</Badge>
            {p.movimento_id && <div className="text-[10px] text-muted-foreground">in Prima Nota</div>}
            {(p.numero_fattura || p.codice_avviso) && <button type="button" onClick={onWebdesk} disabled={!writable} data-testid={`pag-webdesk-${p.id}`}
              className={`flex items-center gap-1 text-[11px] ${p.webdesk_segnato ? "text-green-700 dark:text-green-400" : "text-orange-600"}`}>
              <Globe className="w-3 h-3" />{p.webdesk_segnato ? "Segnata su Webdesk" : "Da segnare su Webdesk"}</button>}
          </div>
        : <Badge variant="outline" className="h-5">Da pagare</Badge>}</td>
      <td><Allegati coll="pagamenti" item={p} onChange={onReload} writable={writable} etichette={["Fattura", "F24", "Ricevuta", "Altro"]} testId={`pag-allegati-${p.id}`} /></td>
      <td className="whitespace-nowrap">{writable && (pagato
        ? <Button size="sm" variant="ghost" className="h-7 gap-1" onClick={onAnnulla} data-testid={`pag-annulla-${p.id}`}><Undo2 className="w-3.5 h-3.5" />Annulla</Button>
        : <>
          <Button size="sm" className="h-7 gap-1 bg-green-600 hover:bg-green-700" onClick={onPaga} data-testid={`pag-paga-${p.id}`}><CircleCheck className="w-3.5 h-3.5" />Paga</Button>
          <Button size="icon" variant="ghost" className="h-7 w-7" onClick={onEdit} data-testid={`pag-edit-${p.id}`}><Pencil className="w-3.5 h-3.5" /></Button>
          <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive" onClick={onDel} data-testid={`pag-delete-${p.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
        </>)}</td>
    </tr>
  );
};
